import { UploadPageLayout } from "components/modules/Layout/UploadPageLayout/UploadPageLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetServerSideProps } from "next";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import Head from "next/head";
import { LanguageProvider } from "client/contexts/LanguageProvider";
import { commonLanguageFiles } from "client/config";
import { translateFunc } from "client/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import dynamic from "next/dynamic";
import React from "react";
import { RouteNames } from "@/RouteNames";
import { ImageConverterSection3Content } from "components/features/ImageConverter/ImageConverterSection3Content";
import { ImageConverterSection4Tutorial } from "components/features/ImageConverter/ImageConverterSection4Tutorial";
import { imageConverterJsonTemplate } from "@/templates/ImageConverterJsonTemplate";
import ImageConverterPreToolsArea from "components/features/ImageConverter/ImageConverterPreToolsArea";
import { TrustPilotSection } from "components/modules/TrustPilot/TrustPilotSection";
import { retry } from "@/utils/retry";
import { PageInfoProvider } from "@/contexts/PageInfoProvider";
import { PageInfo } from "@/modules/ImageConverter/types/PageInfo";
import { getPageInfo } from "@/modules/ImageConverter/queries/getPageInfo";

const ImageConverterUploader = dynamic<{}>(() =>
    import("components/modules/Uploader/Converter/ImageConverterUploader").then((mod) => mod.ImageConverterUploader),
);

const Page = () => {
    const template = imageConverterJsonTemplate;
    const title = translateFunc(template.meta.pageTitle);
    const description = translateFunc(template.meta.metaDescription);
    const metaKeywords = translateFunc(template.meta.metaKeywords);
    return (
        <>
            <Head>
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta name="keywords" content={metaKeywords} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={title} />
                <meta property="twitter:description" content={description} />
            </Head>
            <UploadPageLayout
                navbar={<Navbar url={RouteNames.imageConverter} />}
                title={<Translate keyName={template["section1"]["section1Title"]} />}
                subTitle={<Translate keyName={template["section1"]["section1SubTitle"]} />}
                uploader={<ImageConverterUploader />}
                content={<ImageConverterSection3Content template={template} />}
                tutorial={<ImageConverterSection4Tutorial template={template} />}
                preToolsSlot={<ImageConverterPreToolsArea template={template} />}
                trustPilotSection={
                    <TrustPilotSection title={template.section11?.title} subTitle={template.section11?.subTitle} />
                }
                pageUrl={RouteNames.imageConverter}
            />
        </>
    );
};

const files = ["image-converter-landing", "uploader", ...commonLanguageFiles];

interface PageProps {
    pageInfo: PageInfo;
}

const LanguageWrapper = ({ pageInfo }: PageProps) => {
    return (
        <LanguageProvider files={files}>
            <PageInfoProvider pageInfo={pageInfo}>
                <Page />
            </PageInfoProvider>
        </LanguageProvider>
    );
};

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ locale, res }) => {
    if (res) {
        res.setHeader("Cache-Control", "public, s-maxage=14400, stale-while-revalidate=59");
    }
    const result = await retry(() => getPageInfo("image-converter"));
    if (result.error) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            pageInfo: result.data,
            ...(await serverSideTranslations(locale ?? "", files)),
        },
    };
};

export default LanguageWrapper;
